/** CrmRecordLayoutEditor (Item D / D2c) — the admin-only editor for a list's record-panel layout.
 *
 * A TRANSIENT modal (useState-owned, NOT URL state — per the nav spec, transient editors don't deep
 * link). Reorder fields, group them into named sections, set each section to one or two columns, and
 * hide fields from the panel. Save PUTs the layout through useSetRecordLayout (admin-gated
 * server-side).
 *
 * It initializes from the resolved `layout` the panel already loaded (CrmRecordResponse.layout), so
 * there is no separate fetch / loading state; the backend echoes the resolved layout on save and the
 * record payloads are invalidated, so every open panel re-hydrates.
 *
 * **§15 item 6 (owner, 2026-08-05) added three things**, and one of them changed the model:
 *
 * 1. **Drag to reorder** — the owner's words were "the up/down arrows are painful". Native HTML5
 *    drag-and-drop, no library (this repo has no UI library by convention). Dragging works ACROSS
 *    sections, which the arrows never did — that was the actually-painful part, since moving a field
 *    two sections down meant a dropdown plus a run of arrow clicks.
 * 2. **One or two columns per section.**
 * 3. **Hide a field from the panel.** This is the model change: the editor used to control ORDER +
 *    GROUPING only, and its own hint said "this controls layout, not visibility". Hiding is stored as
 *    an EXCLUSION list, never a visibility whitelist, so a column added tomorrow is visible without
 *    anyone re-saving the layout.
 *
 * ⚠ **The arrows and the section dropdown are KEPT even though the owner finds them painful**, because
 * native drag-and-drop is not keyboard-operable — removing them would make this editor unusable
 * without a mouse. Drag is the primary path; they are the accessible fallback.
 */
import { useMemo, useState } from 'react';
import styles from './crm.module.css';
import type { CrmGridColumn, CrmRecordLayout, CrmRecordLayoutSection } from '../../types';

interface Props {
  /** All columns for the list (the identity `name` is filtered out — it renders in the panel header). */
  columns: CrmGridColumn[];
  /** The current resolved layout (from the record payload). Undefined → the flat default. */
  layout: CrmRecordLayout | undefined;
  onClose: () => void;
  onSave: (sections: CrmRecordLayoutSection[], hiddenFieldKeys: string[]) => void;
  saving: boolean;
  error?: string | null;
}

interface EditSection {
  title: string; // '' = untitled (sent as null)
  fieldKeys: string[];
  columns: 1 | 2;
}

/** Build the initial editing sections from the resolved layout, dropping unknown/`name` keys and
 * appending any column the layout omits into the last section (so nothing is missing at open). */
function toEditSections(columns: CrmGridColumn[], layout: CrmRecordLayout | undefined): EditSection[] {
  const fieldCols = columns.filter((c) => c.field_key !== 'name');
  const valid = new Set(fieldCols.map((c) => c.field_key));
  const secs = layout?.sections;
  if (!secs || secs.length === 0) {
    return [{ title: '', fieldKeys: fieldCols.map((c) => c.field_key), columns: 2 }];
  }
  const placed = new Set<string>();
  const out: EditSection[] = [];
  for (const sec of secs) {
    const keys: string[] = [];
    for (const fk of sec.field_keys) {
      if (valid.has(fk) && !placed.has(fk)) { keys.push(fk); placed.add(fk); }
    }
    out.push({ title: sec.title ?? '', fieldKeys: keys, columns: sec.columns === 1 ? 1 : 2 });
  }
  const leftover = fieldCols.filter((c) => !placed.has(c.field_key)).map((c) => c.field_key);
  if (leftover.length > 0) {
    if (out.length > 0) out[out.length - 1].fieldKeys.push(...leftover);
    else out.push({ title: '', fieldKeys: leftover, columns: 2 });
  }
  return out;
}

/** Where a drag is hovering. `idx === fieldKeys.length` means "after the last field". */
interface DragPos { sec: number; idx: number }

export function CrmRecordLayoutEditor({ columns, layout, onClose, onSave, saving, error }: Props) {
  const labelByKey = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of columns) m[c.field_key] = c.label;
    return m;
  }, [columns]);

  const [sections, setSections] = useState<EditSection[]>(() => toEditSections(columns, layout));
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(layout?.hidden_field_keys ?? []));
  const [dragging, setDragging] = useState<DragPos | null>(null);
  const [dropAt, setDropAt] = useState<DragPos | null>(null);

  // ── section ops ──
  const addSection = () =>
    setSections((s) => [...s, { title: `Section ${s.length + 1}`, fieldKeys: [], columns: 2 }]);
  const renameSection = (idx: number, title: string) =>
    setSections((s) => s.map((sec, i) => (i === idx ? { ...sec, title } : sec)));
  const setSectionColumns = (idx: number, cols: 1 | 2) =>
    setSections((s) => s.map((sec, i) => (i === idx ? { ...sec, columns: cols } : sec)));
  const removeSection = (idx: number) =>
    setSections((s) => {
      if (s.length <= 1) return s; // keep at least one section
      const orphan = s[idx].fieldKeys;
      const rest = s.filter((_, i) => i !== idx);
      // move the removed section's fields into the FIRST remaining section (never lose a field)
      const target = idx === 0 ? 0 : idx - 1;
      const clampedTarget = Math.min(target, rest.length - 1);
      rest[clampedTarget] = {
        ...rest[clampedTarget],
        fieldKeys: [...rest[clampedTarget].fieldKeys, ...orphan],
      };
      return rest;
    });

  /**
   * §5.1 — move a whole SECTION up or down. Arrows, at the owner's explicit request ("this can be
   * simple arrows"), even though they asked for drag on the FIELDS: a section moves rarely and there
   * are a handful of them, so the drop-target ambiguity that makes field arrows painful does not
   * arise here.
   *
   * ⚠ Sections carry their fields with them. That is the whole point — the alternative, moving a
   * section's HEADING and leaving its fields behind, would silently re-parent every one of them.
   */
  const moveSection = (secIdx: number, dir: -1 | 1) =>
    setSections((s) => {
      const to = secIdx + dir;
      if (to < 0 || to >= s.length) return s;
      const next = [...s];
      [next[secIdx], next[to]] = [next[to], next[secIdx]];
      return next;
    });

  // ── field ops ──
  const moveFieldWithinSection = (secIdx: number, fieldIdx: number, dir: -1 | 1) =>
    setSections((s) => s.map((sec, i) => {
      if (i !== secIdx) return sec;
      const to = fieldIdx + dir;
      if (to < 0 || to >= sec.fieldKeys.length) return sec;
      const keys = [...sec.fieldKeys];
      [keys[fieldIdx], keys[to]] = [keys[to], keys[fieldIdx]];
      return { ...sec, fieldKeys: keys };
    }));
  const moveFieldToSection = (fromSec: number, fieldIdx: number, toSec: number) =>
    setSections((s) => {
      if (toSec === fromSec) return s;
      const fk = s[fromSec].fieldKeys[fieldIdx];
      return s.map((sec, i) => {
        if (i === fromSec) return { ...sec, fieldKeys: sec.fieldKeys.filter((_, j) => j !== fieldIdx) };
        if (i === toSec) return { ...sec, fieldKeys: [...sec.fieldKeys, fk] };
        return sec;
      });
    });

  /**
   * Move the dragged field to `to`. Cross-section and within-section in ONE function.
   *
   * ⚠ The removal happens before the insert, and the target index is decremented when both are in the
   * same section and the source sits BEFORE the target — otherwise dragging a field downwards lands it
   * one position short of where it was dropped, which reads as the drag "not quite working" rather than
   * as an off-by-one.
   */
  const dropField = (from: DragPos, to: DragPos) => {
    setSections((s) => {
      const fk = s[from.sec]?.fieldKeys[from.idx];
      if (fk == null) return s;
      const next = s.map((sec) => ({ ...sec, fieldKeys: [...sec.fieldKeys] }));
      next[from.sec].fieldKeys.splice(from.idx, 1);
      let idx = to.idx;
      if (to.sec === from.sec && from.idx < to.idx) idx -= 1;
      const target = next[to.sec];
      target.fieldKeys.splice(Math.max(0, Math.min(idx, target.fieldKeys.length)), 0, fk);
      return next;
    });
  };

  const toggleHidden = (fk: string) =>
    setHidden((h) => {
      const next = new Set(h);
      if (next.has(fk)) next.delete(fk);
      else next.add(fk);
      return next;
    });

  const endDrag = () => { setDragging(null); setDropAt(null); };

  const save = () => {
    // Trim titles; '' → null (untitled). Empty sections are dropped by the backend sanitizer unless
    // titled (a titled placeholder is kept). Send the partition as-is.
    const payload: CrmRecordLayoutSection[] = sections.map((sec) => ({
      title: sec.title.trim() ? sec.title.trim() : null,
      field_keys: sec.fieldKeys,
      columns: sec.columns,
    }));
    // Only keys still in the layout — a field dropped from the list must not linger as hidden. The
    // backend re-sanitizes too; this keeps the request itself honest.
    const known = new Set(sections.flatMap((s) => s.fieldKeys));
    onSave(payload, [...hidden].filter((fk) => known.has(fk)));
  };

  const hiddenCount = sections.flatMap((s) => s.fieldKeys).filter((fk) => hidden.has(fk)).length;

  return (
    <div className={styles.layoutEditorOverlay} role="dialog" aria-modal="true" aria-label="Edit record layout">
      <div className={styles.layoutEditor}>
        <div className={styles.layoutEditorHead}>
          <h3 className={styles.layoutEditorTitle}>Edit record layout</h3>
          <button className={styles.recordPanelClose} onClick={onClose} aria-label="Close layout editor">✕</button>
        </div>
        <p className={styles.layoutEditorHint}>
          Drag fields to reorder them or move them between sections. Set each section to one or two
          columns, and untick a field to hide it from the panel.{' '}
          <strong>A hidden field still appears when it has an AI proposal or a failed sync</strong>, so
          hiding can never bury something that needs a decision.
        </p>

        {error && <div className={styles.recordActionError} role="alert">{error}</div>}

        <div className={styles.layoutEditorBody}>
          {sections.map((sec, si) => (
            <div key={si} className={styles.layoutSection}>
              <div className={styles.layoutSectionHead}>
                <input
                  className={styles.layoutSectionTitle}
                  value={sec.title}
                  placeholder="Untitled section"
                  aria-label={`Section ${si + 1} title`}
                  onChange={(e) => renameSection(si, e.target.value)}
                />
                {/* §5.1: move the whole section, fields and all. Disabled rather than hidden at the
                    ends, so the control set does not reflow as sections move. */}
                <span className={styles.layoutColsToggle} role="group"
                      aria-label={`Move section ${si + 1}`}>
                  <button type="button" className={styles.gridMiniBtn}
                          title="Move this section up" aria-label={`Move section ${si + 1} up`}
                          disabled={si === 0} onClick={() => moveSection(si, -1)}>↑</button>
                  <button type="button" className={styles.gridMiniBtn}
                          title="Move this section down" aria-label={`Move section ${si + 1} down`}
                          disabled={si === sections.length - 1}
                          onClick={() => moveSection(si, 1)}>↓</button>
                </span>
                <span className={styles.layoutColsToggle} role="group"
                      aria-label={`Section ${si + 1} columns`}>
                  {([1, 2] as const).map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={sec.columns === n
                        ? `${styles.gridMiniBtn} ${styles.layoutColsBtnOn}`
                        : styles.gridMiniBtn}
                      aria-pressed={sec.columns === n}
                      title={`${n} column${n > 1 ? 's' : ''}`}
                      onClick={() => setSectionColumns(si, n)}
                    >
                      {n === 1 ? '1 col' : '2 col'}
                    </button>
                  ))}
                </span>
                {sections.length > 1 && (
                  <button className={styles.gridMiniBtn} title="Remove section (fields move up)"
                          aria-label={`Remove section ${si + 1}`} onClick={() => removeSection(si)}>✕</button>
                )}
              </div>

              {/* An empty section still has to be a drop target, or a field can never be moved INTO
                  one — which is how you would add a section and find it unusable. */}
              {sec.fieldKeys.length === 0 && (
                <div
                  className={dropAt?.sec === si
                    ? `${styles.gridEmpty} ${styles.layoutDropInto}` : styles.gridEmpty}
                  onDragOver={(e) => { e.preventDefault(); setDropAt({ sec: si, idx: 0 }); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragging) dropField(dragging, { sec: si, idx: 0 });
                    endDrag();
                  }}
                >
                  No fields in this section — drop one here.
                </div>
              )}

              {sec.fieldKeys.map((fk, fi) => {
                const isDragging = dragging?.sec === si && dragging?.idx === fi;
                const showLine = dropAt?.sec === si && dropAt?.idx === fi;
                const isHidden = hidden.has(fk);
                const label = labelByKey[fk] ?? fk;
                return (
                  <div
                    key={fk}
                    className={[
                      styles.layoutFieldRow,
                      isDragging ? styles.layoutFieldRowDragging : '',
                      showLine ? styles.layoutFieldRowDropBefore : '',
                      isHidden ? styles.layoutFieldRowHidden : '',
                    ].filter(Boolean).join(' ')}
                    draggable
                    onDragStart={(e) => {
                      setDragging({ sec: si, idx: fi });
                      // Firefox will not start a drag without payload on the transfer.
                      e.dataTransfer.setData('text/plain', fk);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragEnd={endDrag}
                    onDragOver={(e) => { e.preventDefault(); setDropAt({ sec: si, idx: fi }); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragging) dropField(dragging, { sec: si, idx: fi });
                      endDrag();
                    }}
                  >
                    <span className={styles.layoutDragHandle} aria-hidden="true" title="Drag to reorder">⠿</span>
                    <input
                      type="checkbox"
                      className={styles.layoutFieldVis}
                      checked={!isHidden}
                      aria-label={`Show ${label} in the panel`}
                      onChange={() => toggleHidden(fk)}
                    />
                    <span className={styles.layoutFieldLabel}>{label}</span>
                    <span className={styles.layoutFieldControls}>
                      {/* Kept as the KEYBOARD path — native drag-and-drop is mouse-only. */}
                      <button className={styles.gridMiniBtn} title="Move up" aria-label={`Move ${label} up`}
                              disabled={fi === 0} onClick={() => moveFieldWithinSection(si, fi, -1)}>↑</button>
                      <button className={styles.gridMiniBtn} title="Move down" aria-label={`Move ${label} down`}
                              disabled={fi === sec.fieldKeys.length - 1}
                              onClick={() => moveFieldWithinSection(si, fi, 1)}>↓</button>
                      {sections.length > 1 && (
                        <select className={styles.layoutSectionPicker} value={si}
                                aria-label={`Move ${label} to section`}
                                onChange={(e) => moveFieldToSection(si, fi, Number(e.target.value))}>
                          {sections.map((s2, i2) => (
                            <option key={i2} value={i2}>{s2.title.trim() || `Section ${i2 + 1}`}</option>
                          ))}
                        </select>
                      )}
                    </span>
                  </div>
                );
              })}

              {/* Drop AFTER the last field. Without this the bottom of a section is unreachable by
                  drag, since every row targets "before me". */}
              {sec.fieldKeys.length > 0 && (
                <div
                  className={dropAt?.sec === si && dropAt?.idx === sec.fieldKeys.length
                    ? `${styles.layoutDropTail} ${styles.layoutDropInto}` : styles.layoutDropTail}
                  onDragOver={(e) => { e.preventDefault(); setDropAt({ sec: si, idx: sec.fieldKeys.length }); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragging) dropField(dragging, { sec: si, idx: sec.fieldKeys.length });
                    endDrag();
                  }}
                />
              )}
            </div>
          ))}
          <button className={styles.gridPageBtn} onClick={addSection}>+ Add section</button>
        </div>

        <div className={styles.layoutEditorFooter}>
          {hiddenCount > 0 && (
            <span className={styles.layoutHiddenCount}>
              {hiddenCount} field{hiddenCount === 1 ? '' : 's'} hidden
            </span>
          )}
          <button className={styles.gridMiniBtn} onClick={onClose}>Cancel</button>
          <button className={styles.gridPageBtn} disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save layout'}
          </button>
        </div>
      </div>
    </div>
  );
}
